import { useHead } from '@unhead/react';
import { Descriptions, Flex } from 'antd';
import classNames from 'classnames';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'translations/hooks';

import {
  GlobalWarning,
  LinesWarning,
  useImportTaskData,
} from 'business/quality-control/pages/segment-import/hooks/data/use-import-task';
import Routes from 'config/routes';
import { useMediaType } from 'technical/media/hooks';
import { PreviousPageButton } from 'ui/button/previous-page-button';
import CapCard from 'ui/cap-card';
import SectionTitle from 'ui/title/sectionTitle';

import style from './index.module.scss';

const GlobalWarningComponent = ({ warning }: { warning: GlobalWarning }) => {
  const { t } = useTranslation();

  return (
    <>
      {t(`qualityControl.segmentImport.warning.error-${warning.label}`, {
        value: warning.values?.join(', '),
      })}
    </>
  );
};

const LinesWarningComponent = ({ warning }: { warning: LinesWarning }) => {
  const { t } = useTranslation();

  return (
    <Descriptions column={1} bordered>
      <Descriptions.Item
        label={t('qualityControl.segmentImport.warning.lineHeader')}
        key={t('qualityControl.segmentImport.warning.lineHeader')}
        style={{ fontWeight: 'bold', backgroundColor: 'white' }}
      >
        {t('qualityControl.segmentImport.warning.warningHeader')}
      </Descriptions.Item>
      {warning.values.map((item) => (
        <Descriptions.Item label={item.line} key={`${item.line}-${item.label}`}>
          {t(`qualityControl.segmentImport.warning.error-${item.label}`)}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
};

export function SegmentImportWarningsPage() {
  const { t } = useTranslation();
  const { isMobile } = useMediaType();

  useHead({
    title: t('qualityControl.segmentImport.warning.title'),
  });

  const [searchParams] = useSearchParams();
  const currentTaskId = searchParams.get('id');

  const { task } = useImportTaskData(currentTaskId ?? '');

  const warnings = task?.warnings ?? {
    type: 'global',
    label: 'technical-error',
  };

  return (
    <Flex
      vertical
      flex={1}
      gap={20}
      align="center"
      className={classNames(style.page, 'page-appear')}
    >
      <SectionTitle
        className={style.backButton}
        leftButton={
          <PreviousPageButton
            to={{ pathname: Routes.QualitySegmentImport }}
            label="qualityControl.segmentImport.cta.goBackImport"
            isMobile={isMobile}
          />
        }
      />
      <CapCard
        title={t('qualityControl.segmentImport.warning.title')}
        className={style.card}
      >
        {warnings.type === 'global' ? (
          <GlobalWarningComponent warning={warnings} />
        ) : (
          <LinesWarningComponent warning={warnings} />
        )}
      </CapCard>
    </Flex>
  );
}
